import React, { useEffect, useState } from 'react';

import {
  loadRazorPayScript,
  openRazorPayPaymentGatewayPopup,
  SUCCESS
} from '@nw/shared/utils';

import { useNiatApplicationMachineContext } from '../../context/NiatApplicationMachineProvider';
import {
  CancelPaymentOrderEvent,
  ResetErrorMessageAndApiStatusEvent
} from '../../types/productMachineTypes';

import JusPayPaymentPopUpModal from '../JusPayPaymentPopUpModal';

const resetSubmitProductOrCreatePaymentStatus: ResetErrorMessageAndApiStatusEvent =
  {
    type: 'RESET_ERROR_MESSAGE_AND_API_STATUS',
    reset: 'STATUS',
    key: 'submitProductOrCreatePaymentApiStatus',
    value: 'IDLE'
  };

export type UsePaymentGatewayHandlerProductMachineSend = (
  event: ResetErrorMessageAndApiStatusEvent | CancelPaymentOrderEvent
) => void;

export interface UsePaymentGatewayHandlerArgs {
  onSuccessPayment?: () => void;
  sectionName: string;
}

export interface UsePaymentGatewayHandlerResult {
  paymentModal: () => React.ReactNode;
  showJusPayPaymentModal: boolean;
}

export const usePaymentGatewayHandler = ({
  onSuccessPayment,
  sectionName
}: UsePaymentGatewayHandlerArgs): UsePaymentGatewayHandlerResult => {
  const { niatApplicationMachineState, niatApplicationMachineSend } =
    useNiatApplicationMachineContext();
  const {
    context: {
      userGraphQlProfileDetails,
      productsSectionMachineRefDetails: {
        [sectionName]: {
          state: {
            context: {
              productDetail,
              productCode,
              apiStatus: {
                getCouponCodeDetailApiStatus,
                submitProductOrCreatePaymentApiStatus
              },
              paymentOrderDetails
            }
          },
          send: productMachineSend
        }
      }
    }
  } = niatApplicationMachineState;
  const [showJusPayPaymentModal, setShowJusPayPaymentModal] = useState(false);

  useEffect(() => {
    if (
      submitProductOrCreatePaymentApiStatus !== 'success' ||
      !paymentOrderDetails
    ) {
      setShowJusPayPaymentModal(false);
      return;
    }

    const amount =
      getCouponCodeDetailApiStatus === SUCCESS &&
      productDetail?.productFeeAfterDiscount !== undefined
        ? productDetail.productFeeAfterDiscount
        : productDetail?.productFee;

    if (paymentOrderDetails.paymentService === 'RAZORPAY' && amount > 0) {
      loadRazorPayScript(
        () =>
          openRazorPayPaymentGatewayPopup({
            userProfileData: userGraphQlProfileDetails,
            paymentOrderDetails,
            productCode,
            productPath: '',
            redirectPath: '',
            requestObject: {},
            values: {
              inviteCode: ''
            },
            onClickCancelOrder: () => {
              productMachineSend(resetSubmitProductOrCreatePaymentStatus);
              productMachineSend({
                type: 'CANCEL_PAYMENT_ORDER',
                orderId: paymentOrderDetails.orderId
              } as CancelPaymentOrderEvent);
            },
            onSuccessPayment: (
              productPath: string,
              redirectPath: string,
              requestObject: unknown
            ) => {},
            productDetails: productDetail
          }),
        () => {
          console.info('razor pay load script error');
        }
      );
    } else if (amount > 0) {
      setShowJusPayPaymentModal(true);
    } else {
      onSuccessPayment?.();
    }
  }, [submitProductOrCreatePaymentApiStatus, paymentOrderDetails]);

  const onJusPayModalClose = (): void => {
    if (!paymentOrderDetails) {
      setShowJusPayPaymentModal(false);
      return;
    }
    productMachineSend(resetSubmitProductOrCreatePaymentStatus);
    productMachineSend({
      type: 'CANCEL_PAYMENT_ORDER',
      orderId: paymentOrderDetails.orderId
    } as CancelPaymentOrderEvent);
    setShowJusPayPaymentModal(false);
  };

  const paymentLink =
    paymentOrderDetails?.jusPayOrderTransactionDetails?.paymentLink;

  const paymentModal = (): React.ReactNode => {
    if (!showJusPayPaymentModal || !paymentOrderDetails || !paymentLink) {
      return null;
    }
    return (
      <JusPayPaymentPopUpModal
        paymentLink={paymentLink}
        onJusPayModalClose={onJusPayModalClose}
        showJusPayPaymentModal={showJusPayPaymentModal}
      />
    );
  };
  return { paymentModal, showJusPayPaymentModal };
};
